/**
 * balance-detection.ts — single source of truth for every tuning knob that
 * shapes how Strategos Universalis resolves "who can see whom".
 *
 * The detection subsystem is by far the most balance-sensitive in the sim —
 * MECHANICS §3 locks the *shape* of the checks (binary vision ring, binary
 * Detection-vs-Stealth scan threshold, cloak gate, grace window) but almost
 * every *number* in them needs playtest feedback to land right. Hiding
 * those numbers inline in advance.ts or the renderer means iterating on
 * feel requires a code search + surgical edits.
 *
 * Rule: anything in this file is fair game to change without touching tests
 * (tests assert the *shape* of the math against these constants, not
 * hard-coded numeric expectations). Anything in advance.ts / detection.ts
 * is structural and requires a real code change.
 *
 * If you find yourself adding a magic number anywhere downstream — pull it
 * back up into this file first.
 */

/**
 * Ticks a target remains "last-known" after leaving team vision. The
 * renderer draws a faded ghost at the last-seen position for this many
 * ticks so the player isn't blind to where a contact was heading; combat
 * targeting also uses this window (MECHANICS §1.4).
 *
 * v3M10.1: migrated to state.balanceData.detection.lastKnownGraceTicks.
 */

/**
 * Ticks the firing ship's effective Stealth drops to 0 after firing any
 * weapon (MECHANICS §2.1). Reserved — combat isn't wired yet, but the
 * helper already honours this so it lights up automatically when firing
 * lands. Only affects the Scan channel under the binary model (vision is
 * stealth-agnostic).
 *
 * v3M10.1: migrated to state.balanceData.detection.firingBreaksStealthTicks.
 */

/**
 * Energy drained per tick by an active cloak. Cloak-capable chassis carry
 * their own `energyCapacity` / `energyRegen` from ships.xlsx; this knob
 * sets the cost side of that economy. Ship uncloaks automatically on
 * `currentEnergy <= 0`.
 *
 * v3M10.1: migrated to state.balanceData.detection.cloakEnergyDrainPerTick.
 */

/**
 * Run the full detection pass every N ticks. v1 keeps this at 1 for
 * correctness; MECHANICS §1.3.3 suggests bumping to 2–3 once ship counts
 * justify the amortisation, with each target assigned a deterministic
 * phase offset. Keep equal to 1 until a perf pass actually needs it.
 */
export const DETECTION_TICK_INTERVAL = 1;

/**
 * Global multipliers on every ship template's authored VisionRange and
 * ScanRange. Lets a balance pass widen or tighten the whole sensor
 * envelope without editing ships.xlsx or re-running the build pipeline.
 * Mirrors how SUBLIGHT_SPEED_SCALE in advance.ts quartered movement
 * without touching the data file.
 */
export const VISION_RANGE_SCALE = 1;
export const SCAN_RANGE_SCALE = 1;

/**
 * Planet vision / scan as static orbital scanners (PLANETS.md).
 *
 * MFix1.5 — flat constants in v1, deliberately size-independent so the
 * tuning surface stays narrow. Numbers chosen to put a planet in the
 * same neighbourhood as a Cruiser (visionRange 0.14, scanRange 0.32 in
 * ships.json) — a planet is a meaningful sensor without dwarfing a
 * scout fleet that flies past it.
 *
 * `planetDetection` is the planet's "see-through-stealth" rating —
 * compared against an enemy ship's effectiveStealth in the detection
 * pass. 1 is mid-range (most ship templates ship 0 / 1).
 *
 * Functions (rather than bare constants) so a future v3 pass can scale
 * by size without touching the call site in scanners.ts.
 *
 * v3M10.1: migrated to state.balanceData.detection.planetVisionRange,
 * planetScanRange, and planetDetectionBase.
 */
import type { PlanetSize, StructuresData, BalanceData } from '../state/types';
export function planetVisionRangeForSize(size: PlanetSize, balance: BalanceData): number {
  void size;
  return balance.detection.planetVisionRange;
}
export function planetScanRangeForSize(size: PlanetSize, balance: BalanceData): number {
  void size;
  return balance.detection.planetScanRange;
}
export function planetDetectionForSize(size: PlanetSize, balance: BalanceData): number {
  void size;
  return balance.detection.planetDetectionBase;
}
export function planetStealthForSize(size: PlanetSize): number {
  void size;
  return 0; // planets don't run stealth — they're stationary big targets
}

/**
 * Per-level LRSA scan boost (v3M1 + STRUCTURES §2.3). Stacks additively
 * on top of the MFix1.5 `PLANET_SCAN_RANGE` baseline — so a planet with
 * an L1 LRSA has effective scanRange 0.32 + 0.30 = 0.62 (above Cruiser
 * scan); an L6 LRSA reaches 0.32 + 1.05 = 1.37 sim-units, system-spanning.
 *
 * v3M14c.2 — curve moved to `state.structuresData.features.lrsa.scanBoostByLevel`.
 * Returns 0 when level is out of [1, 6] (level 0 = under construction,
 * not yet active).
 */
export function lrsaScanBoostForLevel(
  structures: StructuresData,
  level: number,
): number {
  const curve = structures.features.lrsa.scanBoostByLevel;
  if (level < 1 || level > curve.length) return 0;
  return curve[level - 1] ?? 0;
}

/**
 * Canonical tech IDs referenced by the cloak subsystem — NOT balance knobs
 * but co-located here so every cloak-related constant lives in one file.
 * Must match the "Tech Name" column in docs/Tech_Tree.xlsx exactly.
 *
 *  - CLOAKING_TECH_ID: base "Cloaking" tech. Without this, a ship can't
 *    engage cloak even if its template's canCloak flag is true.
 *  - ADVANCED_CLOAKING_TECH_ID: RACE-CC-only "Advanced Cloaking". Grants
 *    the "fire while cloaked" privilege — a firing ship only drops cloak
 *    if its empire has NOT researched this.
 */
export const CLOAKING_TECH_ID = 'Cloaking';
export const ADVANCED_CLOAKING_TECH_ID = 'Advanced Cloaking';

/**
 * Bundled balance shape, for callers that want to pass the whole knob set
 * to a pure helper without importing each constant individually. Helpers
 * accept this so tests can override knobs per-call (e.g. a test-only
 * `visionRangeScale = 2` to verify the scalar is honoured) without
 * touching the module-level defaults.
 */
export interface DetectionBalance {
  lastKnownGraceTicks: number;
  firingBreaksStealthTicks: number;
  cloakEnergyDrainPerTick: number;
  detectionTickInterval: number;
  visionRangeScale: number;
  scanRangeScale: number;
}

/**
 * Module-level defaults bundled into a DetectionBalance. Used by helpers
 * that don't have access to live `state.balanceData` (e.g. tests calling
 * `effectiveStealth` directly without a state argument). These values
 * predate v3M10.1's balance migration and are intentionally distinct
 * from `state.balanceData.detection.*` — that's the live xlsx-driven
 * source of truth for the sim; this record is the fallback for the
 * stateless code paths.
 */
export const DEFAULT_DETECTION_BALANCE: Readonly<DetectionBalance> = {
  lastKnownGraceTicks: 100,
  firingBreaksStealthTicks: 20,
  cloakEnergyDrainPerTick: 1.5,
  detectionTickInterval: DETECTION_TICK_INTERVAL,
  visionRangeScale: VISION_RANGE_SCALE,
  scanRangeScale: SCAN_RANGE_SCALE,
};
